'use client';

import { useQueryClient } from '@tanstack/react-query';
import { differenceInCalendarDays } from 'date-fns';
import { useRouter } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { startTransition } from 'react';
import { flushSync } from 'react-dom';
import { buildRequestSource, stringifySource } from 'utils';
import { formatDashedYYYYMMDD } from 'utils/DateFormats';
import {
  makeHeroRoutesCmsKey,
  makePlaceImagesCmsKey,
  makePlaceNamesCmsKey
} from '~/components/TripOverview/overviewPage/HydratedOverview';
import { preserveScrollOnEnter } from '~/components/TripOverview/ScrollToTopOnEnter';
import { TRPC_SETTINGS } from '~/constants/trpc';
import { api } from '~/libs/api';
import type { TripOverviewLeg } from '~/services/engine-ts';
import { useTripBookingStore } from '~/stores/tpv2/tripBookingStore';
import type { CUSTOMER_INFO_MESSAGE, JOURNEY_EDIT_ERROR } from '~/types/Journey.types';
import { toCustomerInfoMessages } from '~/utils/originDateShiftNotice';
import { useJourneyEditErrorMessage } from './useJourneyEditErrorMessage';
import { REWRITTEN_TRIP_ID_HISTORY_KEY, useTripId } from './useTripId';

type UpdateTripValues = {
  closest_city: string;
  startDate: Date;
  endDate: Date | null;
  adult_count: number;
  child_count: number;
  childAges: number[];
  room_count: number;
  duration: number;
};

export const useUpdateTrip = (sourceName?: string | null) => {
  const tripId = useTripId();
  const router = useRouter();
  const utils = api.useUtils();
  const queryClient = useQueryClient();
  const setActiveTripId = useTripBookingStore((s) => s.setActiveTripId);
  const tFeedback = useTranslations('content.feedback');
  const locale = useLocale();
  const getEditErrorMessage = useJourneyEditErrorMessage();

  const { data: tripOverview } = api.trip.getOverview.useQuery(
    { tripId },
    { ...TRPC_SETTINGS.QUERY_NO_REFETCH, enabled: !!tripId, keepPreviousData: true }
  );
  const { data: origins = [] } = api.cms.origins.useQuery(
    { tripId },
    { ...TRPC_SETTINGS.QUERY_NO_REFETCH, enabled: !!tripId }
  );

  return async (
    overrides: Partial<UpdateTripValues>,
    { scrollToTop = false }: { scrollToTop?: boolean } = {}
  ): Promise<{ error?: string; tripId?: string; customerInfoMessages?: CUSTOMER_INFO_MESSAGE[] }> => {
    if (!tripOverview) return { error: tFeedback('error_generic') };

    const mixes = tripOverview.pax.travellerMixes;
    const currentChildAges = mixes.flatMap((m) => m.childAges);
    const firstLeg = tripOverview.items.find((i): i is TripOverviewLeg => i.type === 'leg');
    const firstItem = tripOverview.items[0];
    const lastItem = tripOverview.items[tripOverview.items.length - 1];
    const tripStartDate = new Date(firstItem?.startDate ?? '');
    const tripEndDate = new Date(lastItem?.endDate ?? '');

    const values: UpdateTripValues = {
      closest_city: firstLeg?.origin.name ?? '',
      startDate: tripStartDate,
      endDate: tripEndDate,
      adult_count: mixes.reduce((sum, m) => sum + m.adultCount, 0),
      child_count: currentChildAges.length,
      childAges: currentChildAges,
      room_count: tripOverview.pax.numberOfRooms,
      duration: differenceInCalendarDays(tripEndDate, tripStartDate) + 1,
      ...overrides
    };

    const childAgesAsNumbers = values.childAges.slice(0, values.child_count).filter((age) => !Number.isNaN(age));
    const originId =
      origins.find((o) => o.locationName === values.closest_city)?.id ?? String(firstLeg?.origin.id ?? '');

    const searchParams = new URLSearchParams(globalThis.window.location.search);
    const response = await fetch('/api/template-journey', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        adult_count: values.adult_count,
        child_count: values.child_count,
        childAges: childAgesAsNumbers,
        room_count: values.room_count,
        closest_city: originId,
        duration: values.duration,
        startDate: formatDashedYYYYMMDD(values.startDate),
        endDate: values.endDate ? formatDashedYYYYMMDD(values.endDate) : null,
        twinRoomPreferred: false,
        journeyId: tripOverview.tripId,
        originLocationId: Number(originId),
        buildParameters: {
          closest_city: originId,
          originLocationId: Number(originId),
          journeyTitleOverride: searchParams.get('title') || undefined
        },
        requestSource:
          searchParams.get('template') === '1'
            ? buildRequestSource({
                environment: process.env.NEXT_PUBLIC_SOURCE_ENV,
                source: searchParams.get('source'),
                lp: searchParams.get('lp'),
                action: 'create'
              })
            : stringifySource({
                environment: process.env.NEXT_PUBLIC_SOURCE_ENV,
                name: sourceName ?? 'blueprint',
                action: 'edit'
              }),
        useYearRoundPricingCache: false
      })
    }).catch(() => null);

    if (!response?.ok) return { error: tFeedback('error_generic') };

    const responseData = await response.json();
    const { failureMessages, uniqueId: newTripId, customerInfoMessages } = responseData.data;

    if (failureMessages?.length) {
      const code = failureMessages[0] as JOURNEY_EDIT_ERROR;
      return { error: getEditErrorMessage(code) || tFeedback('error_generic') };
    }
    const currentPrice = utils.trip.getPrice.getData({ tripId });
    if (currentPrice) {
      utils.trip.getPrice.setData({ tripId: newTripId }, currentPrice, { updatedAt: 0 });
    }

    // Hero routes are computed server-side and keyed by tripId (seeded in
    // HydratedOverview, not client-fetchable). A date/traveller edit doesn't change
    // the route, so carry the current data onto the new id — otherwise the itinerary
    // momentarily renders without its (taller) hero-route cards, which shrinks the
    // page above the fold and makes the scroll jump. Origin edits change the route,
    // so the server reseeds them on navigation instead.
    if (!('closest_city' in overrides)) {
      const heroRoutesCms = queryClient.getQueryData(makeHeroRoutesCmsKey(tripId));
      if (heroRoutesCms) queryClient.setQueryData(makeHeroRoutesCmsKey(newTripId), heroRoutesCms);
      const placeImagesCms = queryClient.getQueryData(makePlaceImagesCmsKey(tripId, locale));
      if (placeImagesCms) queryClient.setQueryData(makePlaceImagesCmsKey(newTripId, locale), placeImagesCms);
      const placeNames = queryClient.getQueryData(makePlaceNamesCmsKey(tripId, locale));
      if (placeNames) queryClient.setQueryData(makePlaceNamesCmsKey(newTripId, locale), placeNames);
    }

    // Fetch the new trip's data BEFORE switching so the overview renders complete in
    // a single commit. Switching first and leaning on keepPreviousData leaves a
    // transient short/empty state that reflows the page and shifts the scroll.
    await Promise.allSettled([
      utils.trip.getOverview.fetch({ tripId: newTripId }),
      utils.trip.getPrice.fetch({ tripId: newTripId }),
      utils.cms.origins.fetch({ tripId: newTripId })
    ]);

    const nextUrl = globalThis.window.location.pathname.replace(tripId, newTripId);
    if ('closest_city' in overrides) {
      preserveScrollOnEnter(nextUrl);
      startTransition(() => {
        setActiveTripId(newTripId);
        router.replace(nextUrl, { scroll: false });
      });
    } else {
      // Dates/travellers don't change the route, so nothing server-rendered goes
      // stale and the new data is already primed above. Swap the URL without a
      // navigation; the store drives useTripId so the overview updates in place.
      // Pass only our own key: spreading the current state carries Next's __NA
      // marker, which makes Next skip adopting the new URL, so its canonicalUrl
      // keeps the pre-edit id and the next router.refresh() rewrites the address
      // bar back to it. Next copies its internals onto the entry itself.
      globalThis.window.history.replaceState({ [REWRITTEN_TRIP_ID_HISTORY_KEY]: newTripId }, '', nextUrl);

      const bookingEl = globalThis.document.getElementById('overview-booking-details');
      const beforeTop = bookingEl?.getBoundingClientRect().top ?? null;
      flushSync(() => setActiveTripId(newTripId));
      if (scrollToTop) {
        globalThis.window.scrollTo({ top: 0, behavior: 'instant' });
      } else if (bookingEl && beforeTop !== null) {
        let stableFrames = 0;
        let totalFrames = 0;
        let aborted = false;
        const abort = () => {
          aborted = true;
        };
        const listenerOpts = { passive: true, capture: true } as const;
        const stop = () => {
          globalThis.window.removeEventListener('wheel', abort, true);
          globalThis.window.removeEventListener('touchmove', abort, true);
        };
        globalThis.window.addEventListener('wheel', abort, listenerOpts);
        globalThis.window.addEventListener('touchmove', abort, listenerOpts);
        const hold = () => {
          if (aborted) {
            stop();
            return;
          }
          const delta = bookingEl.getBoundingClientRect().top - beforeTop;
          const maxScroll = globalThis.document.documentElement.scrollHeight - globalThis.window.innerHeight;
          const target = Math.max(0, Math.min(globalThis.window.scrollY + delta, maxScroll));
          if (Math.abs(target - globalThis.window.scrollY) > 1) {
            globalThis.window.scrollTo({ top: target, behavior: 'instant' });
            stableFrames = 0;
          } else {
            stableFrames += 1;
          }
          totalFrames += 1;
          if (stableFrames < 4 && totalFrames < 90) {
            globalThis.requestAnimationFrame(hold);
          } else {
            stop();
          }
        };
        hold();
      }
    }

    utils.account.isTripSaved.invalidate({ tripId: newTripId });

    return { tripId: newTripId, customerInfoMessages: toCustomerInfoMessages(customerInfoMessages) };
  };
};
